/**
 * web/src/games/GuessMePanel.tsx — Guess Me, composed from its four parts
 * (card eq92fFXqQUuP).
 *
 * The panel renders state, never logic: roles, the daily cap, whether a
 * submission is still editable and what a guess scored are all decided by the
 * server, and every action here is a POST followed by a re-read. Two model
 * calls sit behind the answer and the guess (decoys, judging); neither is
 * visible from this file, and a Plus-gated couple gets the usual upgrade sheet
 * rather than a dead tap.
 */
import { useEffect, useState } from 'react';
import type { GuessMeCategory, GuessMeRound, GuessMeVerdict } from '../../../shared/types';
import { GUESS_ME_CATEGORIES, GUESS_ME_CATEGORY_LABELS, GUESS_ME_DAILY_CAP } from '../../../shared/types';
import { session } from '../shell/bridge';
import { announcePlusRequired } from '../lib/billing';
import { Pressable } from '../ui/Pressable';
import { useRound, useRoundList, submit } from './useRound';
import { RoundCard } from './RoundCard';
import { LockInBar } from './LockInBar';
import { RevealScreen } from './RevealScreen';
import { ResultCard } from './ResultCard';

interface GuessMePanelProps {
  members: { memberId: string; displayName: string }[];
}

/** The wire throws `Error(JSON.stringify({error:{code,message}}))` — pull the
 *  code back out so the panel can react to it instead of guessing from prose. */
function errorCode(e: unknown): string | null {
  if (!(e instanceof Error)) return null;
  try {
    const parsed = JSON.parse(e.message) as { error?: { code?: string } };
    return parsed?.error?.code ?? null;
  } catch {
    return null;
  }
}

const ERROR_COPY: Record<string, string> = {
  cap_reached: `That is all ${GUESS_ME_DAILY_CAP} for today — they start again tomorrow.`,
  not_paired: 'Guess Me needs both of you in the room.',
  not_your_turn: 'It is the other one of you to go this round.',
  already_locked: 'That is already locked in.',
  not_open: 'This round has moved on — reload to catch up.',
};

export function GuessMePanel({ members }: GuessMePanelProps) {
  const me = session();
  const { round, loaded, loadError, gated, reload } = useRound();
  const { rounds: history } = useRoundList(5);
  const [category, setCategory] = useState<GuessMeCategory>('small-stuff');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Category is chosen before EVERY first move of a round, not just the
     couple's first-ever round: the chips silently disappeared after round one
     and the next button reused this browser's last choice — or reset to
     small-stuff on remount (sol review 2026-10-19). Spicy can therefore never
     be reached by accident, only by an explicit tap here. */
  const cats = (
    <div className="gm-cats" role="group" aria-label="Question category">
      {GUESS_ME_CATEGORIES.map(key => (
        <button
          key={key}
          type="button"
          aria-pressed={category === key}
          className={`game-cat${category === key ? ' is-on' : ''}`}
          id={`gm-cat-${key}`}
          onClick={() => setCategory(key)}
        >
          {GUESS_ME_CATEGORY_LABELS[key]}
        </button>
      ))}
    </div>
  );

  const mine = members.find(m => m.memberId === me?.memberId);
  const partner = members.find(m => m.memberId !== me?.memberId);
  const myName = mine?.displayName ?? 'You';
  const partnerName = partner?.displayName ?? 'your partner';

  /* Most actions are fire-and-forget. The two that lock TEXT must report
     success or failure to the drafting bar, which keeps what was typed until
     the server accepts it (sol review 2026-10-19). */
  const act = async (call: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await call();
      setError(null);
      await reload();
    } catch (e) {
      const code = errorCode(e);
      if (code === 'plus_required') {
        announcePlusRequired();
      } else {
        setError(ERROR_COPY[code ?? ''] ?? 'That did not save — try again.');
      }
      console.error('[Pear] guess-me action failed', e);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const run = (call: () => Promise<unknown>) => {
    void act(call).catch(() => {});
  };

  /* Every call names the round it belongs to — a delayed tap must not land on
     the next round (sol review finding 1). `finish` is the retry for a round
     stranded after both locked: no text, just the round id, and the server
     settles the guess it already holds (finding 2). */
  const roundId = round?.roundId ?? '';
  const start = () => run(() => submit('/start', { category }));
  const answer = (text: string) => act(() => submit('/answer', { roundId, text }));
  const guess = (text: string) => act(() => submit('/guess', { roundId, text }));
  const override = (verdict: GuessMeVerdict) => run(() => submit('/override', { roundId, verdict }));
  const finish = () => run(() => submit('/guess', { roundId }));

  const answererName = round?.myRole === 'answerer' ? myName : partnerName;
  const guesserName = round?.myRole === 'guesser' ? myName : partnerName;
  const waiting = round && round.state === 'B_locked';
  /* Scoring normally lands in under a second. The retry only appears once the
     round has been sitting in B_locked long enough to mean the scoring call
     never returned, so the happy path never flashes a button. */
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (!waiting) {
      setStuck(false);
      return;
    }
    const t = window.setTimeout(() => setStuck(true), 3000);
    return () => window.clearTimeout(t);
  }, [waiting, round?.roundId]);

  /* Spicy is opt-in EVERY round, not a sticky state: the moment a round
     reveals, selection returns to the non-spicy default, so reaching Spicy
     always takes a deliberate tap on these very chips (sol review 2026-10-19). */
  useEffect(() => {
    if (round?.state === 'revealed') setCategory('small-stuff');
  }, [round?.roundId, round?.state]);

  /* A Plus-gated first load is not a connection problem: open the upgrade
     sheet the moment we learn the couple is on Essentials (sol review
     2026-10-19). */
  useEffect(() => {
    if (gated) announcePlusRequired();
  }, [gated]);

  return (
    <section className="gm-panel" id="guessme-panel" aria-labelledby="guessme-title">
      <h3 className="game-title" id="guessme-title">
        Guess Me
      </h3>

      {!loaded && !loadError && <p className="game-hint">Loading…</p>}

      {loaded && loadError && !round && (
        <div className="gm-actions">
          <p className="game-error" id="guessme-loaderror">
            Could not load your game — the connection may have dropped.
          </p>
          <Pressable className="pear-btn pear-btn-mustard gm-retry" id="gm-retry" disabled={busy} onClick={() => void reload()}>
            Try again
          </Pressable>
        </div>
      )}

      {loaded && gated && !round && (
        <div className="gm-actions">
          <p className="game-hint" id="guessme-gated">
            Guess Me is part of Pear Plus.
          </p>
          <Pressable className="pear-btn pear-btn-mustard gm-upgrade" id="gm-upgrade" onClick={() => announcePlusRequired()}>
            See Pear Plus
          </Pressable>
        </div>
      )}

      {loaded && !gated && !loadError && !round && (
        <>
          <p className="game-hint" id="guessme-empty">
            One of you answers a question about yourself, the other guesses. Three questions a day,
            and every answer lands in your notebook.
          </p>
          {cats}
          <Pressable
            className="pear-btn pear-btn-mustard gm-start"
            id="gm-start"
            disabled={busy}
            onClick={start}
          >
            Start a question
          </Pressable>
        </>
      )}

      {loaded && round && (
        <>
          <RoundCard round={round} partnerName={partnerName} />

          {round.state === 'open' && round.myRole === 'answerer' && (
            <LockInBar round={round} busy={busy} onAnswer={answer} onGuess={guess} />
          )}

          {round.state === 'A_locked' && round.myRole === 'guesser' && (
            <LockInBar round={round} busy={busy} onAnswer={answer} onGuess={guess} />
          )}

          {waiting && (
            <div className="gm-actions">
              <p className="game-status" id="guessme-scoring">
                Both in — scoring…
              </p>
              {/* Both locked and no verdict yet: the scoring call is the only
                  thing missing, and the server settles the guess it holds. */}
              {stuck && (
                <Pressable
                  className="pear-btn pear-btn-mustard gm-finish"
                  id="gm-finish"
                  disabled={busy}
                  onClick={finish}
                >
                  Reveal it
                </Pressable>
              )}
            </div>
          )}

          {round.state === 'revealed' && (
            <>
              <RevealScreen round={round} answererName={answererName} guesserName={guesserName} />
              <ResultCard round={round} onOverride={override} />
              {round.todayLeft > 0 && (
                <div className="gm-actions">
                  <p className="game-hint">What next? Pick a topic, then start.</p>
                  {cats}
                  <Pressable
                    className="pear-btn pear-btn-blush gm-next"
                    id="gm-next"
                    disabled={busy}
                    onClick={start}
                  >
                    Next · {GUESS_ME_CATEGORY_LABELS[category] ?? category}
                  </Pressable>
                </div>
              )}
            </>
          )}
        </>
      )}

      {error && (
        <p className="game-error" role="alert" id="guessme-error">
          {error}
        </p>
      )}

      {/* The list hook's one job: the rounds already played, newest first. An
          unrevealed round in here shows only your own side — the server has
          already stripped the rest. */}
      {history.length > 0 && (
        <ul className="gm-history" id="gm-history" aria-label="Recently played">
          {history.map(r => (
            <li key={r.roundId} className="gm-history-row" data-verdict={r.verdict ?? 'open'}>
              <span className="gm-history-q">{r.question}</span>
              <span className="gm-history-v">
                {r.verdict ? `${r.verdict} · +${r.score}` : r.state === 'revealed' ? '' : 'in play'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
